import { useState, useEffect } from 'react';
import { Search, Bell, Download, Filter, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../hooks/useAuth';
import { ContentRecord, ContentStatus } from '../../types/content';
import { Badge, EmptyState } from '../shared';

type SortField = 'title' | 'contentType' | 'status' | 'aiReadinessScore' | 'updatedAt';
type SortDirection = 'asc' | 'desc';

export function Records() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState<ContentRecord[]>([]);
  const [filteredRecords, setFilteredRecords] = useState<ContentRecord[]>([]);
  const [statusFilter, setStatusFilter] = useState<ContentStatus | 'all'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<SortField>('updatedAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [selectedRecords, setSelectedRecords] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadRecords();
  }, [user]);

  useEffect(() => {
    filterAndSortRecords();
  }, [records, statusFilter, searchTerm, sortField, sortDirection]);

  const loadRecords = async () => {
    if (!user) return;

    try {
      const q = query(
        collection(db, 'contentRecords'),
        where('userId', '==', user.uid)
      );
      const snapshot = await getDocs(q);
      setRecords(snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }) as ContentRecord));
    } catch (error) {
      console.error('Error loading records:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterAndSortRecords = () => {
    let filtered = records;

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(r => r.status === statusFilter);
    }

    // Apply search
    if (searchTerm) {
      filtered = filtered.filter(r =>
        r.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        r.contentType.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aVal: any = a[sortField];
      let bVal: any = b[sortField];

      if (sortField === 'updatedAt' && aVal && bVal) {
        aVal = aVal.toDate().getTime();
        bVal = bVal.toDate().getTime();
      }

      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    setFilteredRecords(filtered);
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const toggleSelectAll = () => {
    if (selectedRecords.size === filteredRecords.length) {
      setSelectedRecords(new Set());
    } else {
      setSelectedRecords(new Set(filteredRecords.map(r => r.id)));
    }
  };

  const toggleSelectRecord = (id: string) => {
    const newSelected = new Set(selectedRecords);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedRecords(newSelected);
  };

  const exportToCSV = () => {
    const headers = ['Title', 'Type', 'Status', 'AI Readiness', 'Score', 'URL', 'Last Updated'];
    const rows = filteredRecords.map(r => [
      r.title,
      r.contentType,
      r.status,
      r.aiReadiness,
      r.aiReadinessScore.toString(),
      r.url || '',
      r.updatedAt.toDate().toLocaleDateString()
    ]);

    const csv = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `content-records-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const statusCounts = {
    all: records.length,
    approved: records.filter(r => r.status === 'approved').length,
    pending: records.filter(r => r.status === 'pending').length,
    suppressed: records.filter(r => r.status === 'suppressed').length,
    expiring: records.filter(r => r.status === 'expiring').length,
    flagged: records.filter(r => r.status === 'flagged').length,
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-t2-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Navigation Bar */}
      <div className="bg-shell-navy border-b border-gray-700 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <span className="text-gray-400 text-sm">Content</span>
          <span className="text-gray-600">/</span>
          <span className="text-white text-sm font-medium">Records</span>
        </div>

        <div className="flex items-center gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search records..."
              className="pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white placeholder-gray-500 w-64 focus:outline-none focus:border-t2-primary"
            />
          </div>
          <span className="px-3 py-1 bg-orange-600 text-white text-xs font-semibold rounded uppercase">Growth</span>
          <button className="relative p-2 text-gray-400 hover:text-white">
            <Bell className="w-5 h-5" />
            <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-6 py-8">
        <div className="mb-6 flex items-start justify-between">
          <div>
            <p className="text-xs font-semibold text-t2-primary uppercase tracking-wide mb-1">Kodiac Content</p>
            <h1 className="text-3xl font-semibold text-gray-900">Records</h1>
            <p className="text-sm text-gray-600 mt-1">View and manage all synced content records</p>
          </div>

          <button
            onClick={exportToCSV}
            disabled={filteredRecords.length === 0}
            className="flex items-center gap-2 px-4 py-2 bg-t2-primary text-white rounded-lg hover:bg-t2-dark disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
        </div>

        {/* Status Filter Tabs */}
        <div className="flex items-center gap-2 mb-6 overflow-x-auto">
          {(['all', 'approved', 'pending', 'suppressed', 'expiring', 'flagged'] as const).map(status => (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                statusFilter === status
                  ? 'bg-t2-primary text-white'
                  : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
              }`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)} ({statusCounts[status]})
            </button>
          ))}
        </div>

        {/* Bulk Actions */}
        {selectedRecords.size > 0 && (
          <div className="bg-t2-primary text-white rounded-lg p-4 mb-4 flex items-center justify-between">
            <span className="text-sm font-medium">{selectedRecords.size} records selected</span>
            <div className="flex items-center gap-2">
              <button className="px-3 py-1 bg-white text-t2-primary rounded text-sm font-medium hover:bg-gray-100">
                Approve
              </button>
              <button className="px-3 py-1 bg-white text-t2-primary rounded text-sm font-medium hover:bg-gray-100">
                Suppress
              </button>
              <button className="px-3 py-1 bg-white text-t2-primary rounded text-sm font-medium hover:bg-gray-100">
                Delete
              </button>
            </div>
          </div>
        )}

        {/* Records Table */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
          {filteredRecords.length === 0 ? (
            <div className="p-12">
              <EmptyState
                icon={Filter}
                title={statusFilter === 'all' ? 'No records yet' : `No ${statusFilter} records`}
                description={
                  statusFilter === 'all'
                    ? 'Connect a source to start syncing content'
                    : `No records with status: ${statusFilter}`
                }
              />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-surface-warm border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={selectedRecords.size === filteredRecords.length}
                        onChange={toggleSelectAll}
                        className="rounded border-gray-300"
                      />
                    </th>
                    <th
                      onClick={() => handleSort('title')}
                      className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase cursor-pointer hover:bg-gray-100"
                    >
                      <div className="flex items-center gap-1">
                        Title
                        {sortField === 'title' && (
                          sortDirection === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                        )}
                      </div>
                    </th>
                    <th
                      onClick={() => handleSort('contentType')}
                      className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase cursor-pointer hover:bg-gray-100"
                    >
                      <div className="flex items-center gap-1">
                        Type
                        {sortField === 'contentType' && (
                          sortDirection === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                        )}
                      </div>
                    </th>
                    <th
                      onClick={() => handleSort('status')}
                      className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase cursor-pointer hover:bg-gray-100"
                    >
                      <div className="flex items-center gap-1">
                        Status
                        {sortField === 'status' && (
                          sortDirection === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                        )}
                      </div>
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">
                      AI Readiness
                    </th>
                    <th
                      onClick={() => handleSort('aiReadinessScore')}
                      className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase cursor-pointer hover:bg-gray-100"
                    >
                      <div className="flex items-center gap-1">
                        Score
                        {sortField === 'aiReadinessScore' && (
                          sortDirection === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                        )}
                      </div>
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">
                      Gaps
                    </th>
                    <th
                      onClick={() => handleSort('updatedAt')}
                      className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase cursor-pointer hover:bg-gray-100"
                    >
                      <div className="flex items-center gap-1">
                        Updated
                        {sortField === 'updatedAt' && (
                          sortDirection === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                        )}
                      </div>
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredRecords.map(record => (
                    <tr key={record.id} className="hover:bg-surface-warm transition-colors">
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedRecords.has(record.id)}
                          onChange={() => toggleSelectRecord(record.id)}
                          className="rounded border-gray-300"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-gray-900 max-w-xs truncate">
                          {record.title}
                        </div>
                        {record.url && (
                          <a
                            href={record.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-t2-primary hover:underline flex items-center gap-1"
                          >
                            View <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant="primary">{record.contentType}</Badge>
                      </td>
                      <td className="px-4 py-3">
                        <Badge
                          variant={
                            record.status === 'approved'
                              ? 'success'
                              : record.status === 'suppressed'
                              ? 'critical'
                              : 'warning'
                          }
                        >
                          {record.status}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        <Badge
                          variant={
                            record.aiReadiness === 'excellent'
                              ? 'success'
                              : record.aiReadiness === 'good'
                              ? 'primary'
                              : record.aiReadiness === 'needsWork'
                              ? 'warning'
                              : 'critical'
                          }
                        >
                          {record.aiReadiness}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm font-semibold text-t2-primary">
                          {record.aiReadinessScore}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-xs text-gray-600">
                          {record.gaps.length > 0 ? (
                            <span className="text-warning-amber">{record.gaps.length} gaps</span>
                          ) : (
                            <span className="text-success-green">No gaps</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-xs text-gray-600">
                          {record.updatedAt.toDate().toLocaleDateString()}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <button className="text-sm text-t2-primary hover:text-t2-dark">
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination (placeholder) */}
        {filteredRecords.length > 0 && (
          <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
            <span>Showing {filteredRecords.length} of {records.length} records</span>
            <div className="flex items-center gap-2">
              <button className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-50" disabled>
                Previous
              </button>
              <button className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-50" disabled>
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
